// src/App.js
import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';

// Pages
import HomePage from './pages/HomePage';
import ElectorateDetails from './pages/ElectorateDetails';
import ComparisonPage from './pages/ComparisonPage';
import AboutPage from './pages/AboutPage';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import LoadingIndicator from './components/layout/LoadingIndicator';

// Import JSON data directly
import electoratesData from './data/electorates_summary.json';
import statesData from './data/states.json';
import agenciesData from './data/agencies.json';
import similarElectoratesData from './data/similar_electorates.json';

// Utilities
import { getCachedData, setCachedData, CACHE_KEYS } from './utils/cacheUtils';
import { logError, getUserFriendlyMessage } from './utils/errorUtils';
import { markStart, markEnd, PERFORMANCE_MARKERS } from './utils/performanceUtils';

console.log('App.js loaded');

// Only log in development environment
const isDev = process.env.NODE_ENV === 'development';
const logDebug = isDev ? console.log : () => {};

// Add custom animation keyframes to the document
const addAnimationStyles = () => {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes scaleIn {
      from { transform: scale(0.9); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    
    @keyframes slideInFromBottom {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    
    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
      70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
      100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
    }
    
    .animate-fadeIn {
      animation: fadeIn 0.3s ease-out forwards;
    }
    
    .animate-scaleIn {
      animation: scaleIn 0.3s ease-out forwards;
    }
    
    .animate-slideInFromBottom {
      animation: slideInFromBottom 0.4s ease-out forwards;
    }
    
    .animate-pulseEffect {
      animation: pulse 2s infinite;
    }
  `;
  document.head.appendChild(style);
};

function App() {
  console.log('App component rendering');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [electorates, setElectorates] = useState([]);
  const [states, setStates] = useState({});
  const [agencies, setAgencies] = useState([]);
  const [similarityData, setSimilarityData] = useState({});
  const [error, setError] = useState(null);
  
  // Add custom animations on first render
  useEffect(() => {
    addAnimationStyles();
  }, []);
  
  useEffect(() => {
    console.log('App useEffect running');
    // Load initial application data from directly imported JSON
    const loadInitialData = () => {
      try {
        console.log('loadInitialData function running');
        setLoading(true);
        console.log("Starting to load initial data from imports...");
        
        // Validate imported data
        if (!electoratesData || !electoratesData.length) {
          throw new Error('Electorates data is missing or empty');
        }
        
        if (!statesData || !Object.keys(statesData).length) {
          throw new Error('States data is missing or empty');
        }
        
        // Set data from imported JSON
        console.log("Setting electorates data:", electoratesData.length, "items");
        setElectorates(electoratesData);
        
        console.log("Setting states data:", Object.keys(statesData).length, "states");
        setStates(statesData);
        
        console.log("Setting agencies data:", agenciesData.length, "items");
        setAgencies(agenciesData);
        
        console.log("Setting similarity data:", Object.keys(similarElectoratesData).length, "entries");
        setSimilarityData(similarElectoratesData);
        
        setError(null);
        console.log("All data loaded successfully");
        
      } catch (err) {
        console.error('Failed to load initial data:', err);
        setError(`Failed to load application data: ${err.message}. Please check console for details and try refreshing the page.`);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);
  
  // Memoize application data to prevent unnecessary re-renders
  const appData = useMemo(() => ({
    electorates,
    states,
    agencies,
    similarityData,
    navigate // Include navigate for routing from components
  }), [electorates, states, agencies, similarityData, navigate]);
  
  if (loading) {
    return (
      <div className="dark-theme min-h-screen flex items-center justify-center">
        <LoadingIndicator message="Loading electoral grants data..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 dark-theme">
        <div className="p-6 max-w-sm bg-gray-800 rounded-lg border border-red-900 shadow-xl animate-scaleIn">
          <h2 className="text-xl font-bold text-red-400 mb-3 font-sora">Error</h2>
          <p className="text-gray-300 font-inter">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors font-inter"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col min-h-screen bg-gray-900 dark-theme">
      <Header navigate={navigate} />
      
      <main className="flex-grow container mx-auto px-4 pt-24 pb-8 animate-fadeIn">
        <Routes>
          <Route 
            path="/" 
            element={<HomePage appData={appData} />} 
          />
          <Route 
            path="/electorate/:name" 
            element={<ElectorateDetails appData={appData} />} 
          />
          <Route 
            path="/compare/:electorateNames" 
            element={<ComparisonPage appData={appData} />} 
          />
          <Route 
            path="/about" 
            element={<AboutPage />} 
          />
        </Routes>
      </main>
      
      <Footer />
    </div>
  );
}

export default App;